import { applyPatches, type Patch } from "immer";
import * as Sentry from "@sentry/nextjs";
import type { Getter, Setter } from "jotai";
import type { Edge } from "@xyflow/react";
import type { NoxusReactflowNodeV2 } from "@/types/spotflow-v2";
import { edgesAtom, nodesAtom } from "../atoms";
import { markDirty } from "../helpers";
import { isApplyingHistoryAtom } from "./atoms";
import type { AtomPatchPair, Transaction } from "./types";

/**
 * Convert an id-keyed patch (`byId/<id>/...`) back to an array-index patch
 * against the current array state. Add/remove ops on the top level are
 * handled separately because they don't address an existing entity by id
 * yet — they carry the new value (add) or are addressing-by-index (remove).
 */
function denormalizePatch(
  patch: Patch,
  state: ReadonlyArray<{ id: string }>
): Patch | null {
  if (patch.path.length === 0) return patch;

  const head = patch.path[0];
  if (head !== "byId") return patch;

  const id = patch.path[1];
  if (typeof id !== "string") return null;

  const index = state.findIndex((entity) => entity.id === id);
  if (index === -1) return null;

  return {
    ...patch,
    path: [index, ...patch.path.slice(2)],
  };
}

function denormalizePatches(
  patches: Patch[],
  state: ReadonlyArray<{ id: string }>
): Patch[] {
  const out: Patch[] = [];
  for (const p of patches) {
    const denorm = denormalizePatch(p, state);
    if (denorm) out.push(denorm);
  }
  return out;
}

function applyPair<T extends { id: string }>(
  current: ReadonlyArray<T>,
  pair: AtomPatchPair,
  direction: "undo" | "redo"
): ReadonlyArray<T> {
  const patches = direction === "undo" ? pair.undo : pair.redo;
  const denorm = denormalizePatches(patches, current);
  return applyPatches(current as T[], denorm);
}

/**
 * Apply a transaction's patches to the tracked atoms. Sets
 * `isApplyingHistoryAtom` for the duration so action atoms don't record their
 * own write back into history. Marks the editor dirty so the autosave path
 * picks up the revert.
 *
 * Selection state is intentionally NOT restored — the undo/redo entry points
 * close the config drawer (selection → null) before calling apply.
 *
 * If patch application throws (e.g. malformed patches in an old stack entry),
 * the error is reported to Sentry and the apply is silently skipped — the
 * editor stays consistent with whatever atoms hold today rather than
 * crashing.
 */
export function applyTransaction(
  get: Getter,
  set: Setter,
  txn: Transaction,
  direction: "undo" | "redo"
): void {
  set(isApplyingHistoryAtom, true);
  try {
    if (txn.nodes) {
      const next = applyPair<NoxusReactflowNodeV2>(
        get(nodesAtom),
        txn.nodes,
        direction
      );
      set(nodesAtom, next as NoxusReactflowNodeV2[]);
    }
    if (txn.edges) {
      const next = applyPair<Edge>(get(edgesAtom), txn.edges, direction);
      set(edgesAtom, next as Edge[]);
    }

    markDirty(set);
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { label: txn.label, direction, phase: "apply" },
    });
    console.error("[history] failed to apply transaction:", txn.label, err);
  } finally {
    set(isApplyingHistoryAtom, false);
  }
}
